import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCadProjects, getSharedWithMeCadProjects } from '../../services/cadService';
import { ownedCadProjectsForLists } from '../utils/cadCollectionsForLists';

/**
 * Showrooms del panel Listas.
 * Mis listas: `GET /getcadprojects` (propio, o `?owner_user_id=` en espejo).
 * Compartidas: `GET /getsharedwithmecadprojects` (allowlist restricted).
 *
 * @param {{ enabled?: boolean, ownerUserId?: string|null }} [options]
 *        Con `ownerUserId`, Mis listas CAD piden el espejo del owner (espacio)
 *        y **no** se piden compartidas conmigo (son del JWT, no del espacio).
 */
export function useCadCollectionsForLists({ enabled = true, ownerUserId = null } = {}) {
  const [ownedRaw, setOwnedRaw] = useState([]);
  const [shared, setShared] = useState([]);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((key) => key + 1), []);

  useEffect(() => {
    if (!enabled) {
      setOwnedRaw([]);
      setShared([]);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);

    (async () => {
      try {
        const spaceOwnerId = ownerUserId ? String(ownerUserId).trim() : '';
        const [ownedResult, sharedResult] = await Promise.all([
          getCadProjects({
            signal: controller.signal,
            ...(spaceOwnerId ? { ownerUserId: spaceOwnerId } : {}),
          }),
          // Compartidas por colección = del JWT. En espacio ajeno no se mezclan.
          spaceOwnerId
            ? Promise.resolve({ ok: true, projects: [] })
            : getSharedWithMeCadProjects({ signal: controller.signal }),
        ]);
        if (controller.signal.aborted) return;
        setOwnedRaw(ownedResult.ok ? ownedResult.projects : []);
        setShared(sharedResult.ok ? sharedResult.projects : []);
      } catch (error) {
        if (error?.name === 'AbortError') return;
        console.error('CAD showroom: no se pudieron cargar colecciones para listas', error);
        setOwnedRaw([]);
        setShared([]);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [enabled, reloadKey, ownerUserId]);

  const owned = useMemo(
    () => ownedCadProjectsForLists(ownedRaw, { spaceMirror: Boolean(ownerUserId) }),
    [ownedRaw, ownerUserId],
  );

  return { loading, owned, shared, reload };
}
